import { worldPoints, bbox, polysIntersect, polyDistance, selfIntersects, fmtIn } from "./geometry";

export const CHECKS = [
  { code: "path", label: "Path closure & validity" },
  { code: "scale", label: "Scale sanity" },
  { code: "bounds", label: "Roll bounds & margins" },
  { code: "overlap", label: "Overlap check" },
  { code: "spacing", label: "Spacing tolerance" },
  { code: "tolerance", label: "Edit tolerance (±1/16\")" },
  { code: "machine", label: "Machine compatibility" },
];

export function validateLayout({ pieces, rollWidth, margin, spacing, machine }) {
  const issues = [];
  const push = (level, code, message, pieceIds = []) => issues.push({ level, code, message, pieceIds });
  const included = pieces.filter((p) => p.included);
  if (!included.length) push("warn", "path", "No pieces on the roll");
  const worlds = included.map((p) => { const w = worldPoints(p); return { p, w, bb: bbox(w) }; });

  for (const { p, w, bb } of worlds) {
    if (w.length < 3) push("error", "path", `${p.name}: open path (fewer than 3 nodes)`, [p.id]);
    else if (selfIntersects(w)) push("error", "path", `${p.name}: self-intersecting outline`, [p.id]);
    if (bb.minX < margin - 1e-6 || bb.maxX > rollWidth - margin + 1e-6 || bb.minY < margin - 1e-6)
      push("error", "bounds", `${p.name}: outside roll usable area (${fmtIn(margin)} margin)`, [p.id]);
    if (bb.w > 72 || bb.h > 72) push("warn", "scale", `${p.name}: ${fmtIn(bb.w)} × ${fmtIn(bb.h)} looks oversized — check import units`, [p.id]);
    if ((bb.w < 3 || bb.h < 3) && !(p.setKey && p.setOffset) && !/^ppf_/.test(p.pieceType || "")) push("warn", "scale", `${p.name}: ${fmtIn(bb.w)} × ${fmtIn(bb.h)} looks undersized — check import units`, [p.id]); // a trim strip / sensor band inside a DigiCut kit is meant to be small
    if (Math.abs(p.appliedOffset || 0) > 0.25) push("warn", "tolerance", `${p.name}: edge offset ${fmtIn(p.appliedOffset)} exceeds safe range`, [p.id]);
    if (p.dirty) push("warn", "tolerance", `${p.name}: geometry edited — verify against glass before cutting`, [p.id]);
  }
  for (let i = 0; i < worlds.length; i++) {
    for (let j = i + 1; j < worlds.length; j++) {
      const a = worlds[i], b = worlds[j];
      if (polysIntersect(a.w, b.w)) push("error", "overlap", `${a.p.name} overlaps ${b.p.name}`, [a.p.id, b.p.id]);
      else if (polyDistance(a.w, b.w) < spacing - 1 / 32 && !(a.p.setKey && a.p.setKey === b.p.setKey && a.p.setOffset && b.p.setOffset)) push("warn", "spacing", `${a.p.name} ↔ ${b.p.name} gap under ${fmtIn(spacing)}`, [a.p.id, b.p.id]); // parts of one file sit where the file put them
    }
  }
  if (!machine) push("warn", "machine", "No plotter profile selected");
  else {
    if (rollWidth > machine.max_cut_width_in) push("error", "machine", `${fmtIn(rollWidth)} roll exceeds ${machine.name} max cut width ${fmtIn(machine.max_cut_width_in)}`);
    if (!machine.certified) push("warn", "machine", `${machine.name} is not yet certified`);
  }
  return issues;
}

export function summarize(issues) {
  return CHECKS.map((c) => {
    const rel = issues.filter((i) => i.code === c.code);
    const status = rel.some((i) => i.level === "error") ? "FAIL" : rel.length ? "WARN" : "PASS";
    return { ...c, status, issues: rel };
  });
}
